//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import {
  CarouselNavigationPosition,
  CtaButtonStyle,
  IExperienceDto,
  IconStyle,
  PlatformType,
} from '@apis/experiences/experiencesApiModels';
import { ISelfServeGallery } from '@apis/galleries/galleriesApiModels';

import { Locales } from 'app/constants/locales/locale';
import { SalesforceLocales } from 'app/constants/locales/salesforceLocale';

// Explicit mapping from site Locales to their SalesforceLocales equivalent.
// Kept separate from enum-key matching so renaming/adding a key in either
// enum can't silently break this relationship.
export const LocaleToSalesforceLocale: Record<Locales, SalesforceLocales> = {
  [Locales.CS_CZ]: SalesforceLocales.CS_CZ,
  [Locales.DA_DK]: SalesforceLocales.DA_DK,
  [Locales.DE_DE]: SalesforceLocales.DE_DE,
  [Locales.EN_CA]: SalesforceLocales.EN_CA,
  [Locales.EN_EU]: SalesforceLocales.EN_EU,
  [Locales.EN_GB]: SalesforceLocales.EN_GB,
  [Locales.EN_US]: SalesforceLocales.EN_US,
  [Locales.ES_ES]: SalesforceLocales.ES_ES,
  [Locales.ES_MX]: SalesforceLocales.ES_MX,
  [Locales.ES_US]: SalesforceLocales.ES_US,
  [Locales.FI_FI]: SalesforceLocales.FI_FI,
  [Locales.FR_CA]: SalesforceLocales.FR_CA,
  [Locales.FR_FR]: SalesforceLocales.FR_FR,
  [Locales.HU_HU]: SalesforceLocales.HU_HU,
  [Locales.ID_ID]: SalesforceLocales.ID_ID,
  [Locales.IT_IT]: SalesforceLocales.IT_IT,
  [Locales.ET_EE]: SalesforceLocales.ET_EE,
  [Locales.JA_JP]: SalesforceLocales.JA_JP,
  [Locales.KO_KR]: SalesforceLocales.KO_KR,
  [Locales.LT_LT]: SalesforceLocales.LT_LT,
  [Locales.LV_LV]: SalesforceLocales.LV_LV,
  [Locales.NL_NL]: SalesforceLocales.NL_NL,
  [Locales.PL_PL]: SalesforceLocales.PL_PL,
  [Locales.PT_BR]: SalesforceLocales.PT_BR,
  [Locales.RO_RO]: SalesforceLocales.RO_RO,
  [Locales.RU_RU]: SalesforceLocales.RU_RU,
  [Locales.SV_SE]: SalesforceLocales.SV_SE,
  [Locales.TR_TR]: SalesforceLocales.TR_TR,
  [Locales.ZH_CN]: SalesforceLocales.ZH_CN,
};

export enum FanreelCustomizationActionType {
  CLEAR_SNIPPET = 0,
  DEPLOY = 1,
  DEPLOYMENT_COMPLETE = 2,
  GENERATE_PREVIEW = 3,
  LOAD = 4,
  NEXT_PAGE = 5,
  PREV_PAGE = 6,
  SET_PREVIEW_SNIPPET = 7,
  SET_PAGE = 8,
  START_ASYNC = 9,
  STOP_ASYNC = 10,
  UPDATE_CUSTOMIZATION = 11,
  UPDATE_HAS_UNSAVED_CHANGES = 12,
}

export enum FanreelCustomizationPages {
  MEDIA = 'Media',
  LAYOUT = 'Layout',
  CUSTOMIZE = 'Customize',
  UPLOADER = 'Uploader',
  LOCALIZE = 'Localize',
  SETUP = 'Setup',
}

export enum FanreelCustomizationSortValues {
  MODERATION = 'Moderation',
  OPTIMIZED = 'Optimized',
  LATEST = 'Latest',
}

export const FanreelCustomizationSortLabels = {
  [FanreelCustomizationSortValues.MODERATION]: 'Moderation Time',
  [FanreelCustomizationSortValues.OPTIMIZED]: 'Optimized',
  [FanreelCustomizationSortValues.LATEST]: 'Posted Time',
};

export function generateCustomizationSortOptions() {
  return [
    {
      label:
        FanreelCustomizationSortLabels[
          FanreelCustomizationSortValues.MODERATION
        ],
      value: FanreelCustomizationSortValues.MODERATION,
    },
    {
      label:
        FanreelCustomizationSortLabels[
          FanreelCustomizationSortValues.OPTIMIZED
        ],
      value: FanreelCustomizationSortValues.OPTIMIZED,
    },
    {
      label:
        FanreelCustomizationSortLabels[FanreelCustomizationSortValues.LATEST],
      value: FanreelCustomizationSortValues.LATEST,
    },
  ];
}

export enum FanreelCustomizationImageAspectRatios {
  ORIGINAL = 'Original',
  SQUARE = 'Square',
}

export enum FanreelCustomizationCtaType {
  FILLED = 'Filled Button',
  OUTLINED = 'Outlined Button',
  TEXT = 'Text Only',
}

export function generateCustomizationCtaTypeOptions() {
  return [
    {
      label: FanreelCustomizationCtaType.FILLED,
      value: FanreelCustomizationCtaType.FILLED,
    },
    {
      label: FanreelCustomizationCtaType.OUTLINED,
      value: FanreelCustomizationCtaType.OUTLINED,
    },
    {
      label: FanreelCustomizationCtaType.TEXT,
      value: FanreelCustomizationCtaType.TEXT,
    },
  ];
}

export const FanreelCustomizationButtonShapeLabels = {
  [CtaButtonStyle.PILL]: 'Pill',
  [CtaButtonStyle.RECTANGLE]: 'Rectangle',
};

export function generateCustomizationButtonShapeOptions() {
  return [
    {
      label: FanreelCustomizationButtonShapeLabels[CtaButtonStyle.RECTANGLE],
      value: CtaButtonStyle.RECTANGLE,
    },
    {
      label: FanreelCustomizationButtonShapeLabels[CtaButtonStyle.PILL],
      value: CtaButtonStyle.PILL,
    },
  ];
}

export const FanreelCustomizationCarouselNavigationPositionLabels = {
  [CarouselNavigationPosition.INLINE_OVERLAP]: 'Inside Carousel',
  [CarouselNavigationPosition.INLINE_OUTSIDE]: 'Outside Carousel',
};

export function generateCustomizationCarouselNavigationPositionOptions() {
  return [
    {
      label:
        FanreelCustomizationCarouselNavigationPositionLabels[
          CarouselNavigationPosition.INLINE_OUTSIDE
        ],
      value: CarouselNavigationPosition.INLINE_OUTSIDE,
    },
    {
      label:
        FanreelCustomizationCarouselNavigationPositionLabels[
          CarouselNavigationPosition.INLINE_OVERLAP
        ],
      value: CarouselNavigationPosition.INLINE_OVERLAP,
    },
  ];
}

export enum FanreelCustomizationCarouselNavigationArrowStyle {
  ARROW = 'Arrow',
  ARROW_WITH_BUTTON = 'Arrow with Button',
}

export function generateCustomizationCarouselNavigationArrowStyleOptions() {
  return [
    {
      label: FanreelCustomizationCarouselNavigationArrowStyle.ARROW,
      value: FanreelCustomizationCarouselNavigationArrowStyle.ARROW,
    },
    {
      label: FanreelCustomizationCarouselNavigationArrowStyle.ARROW_WITH_BUTTON,
      value: FanreelCustomizationCarouselNavigationArrowStyle.ARROW_WITH_BUTTON,
    },
  ];
}

export const FanreelCustomizationCarouselNavigationButtonShapeLabels = {
  [IconStyle.ROUND]: 'Rounded',
  [IconStyle.RECTANGLE]: 'Rectangle',
};

export function generateCustomizationCarouselNavigationButtonShapeOptions() {
  return [
    {
      label:
        FanreelCustomizationCarouselNavigationButtonShapeLabels[
          IconStyle.ROUND
        ],
      value: IconStyle.ROUND,
    },
    {
      label:
        FanreelCustomizationCarouselNavigationButtonShapeLabels[
          IconStyle.RECTANGLE
        ],
      value: IconStyle.RECTANGLE,
    },
  ];
}

function isLocale(value: string): value is Locales {
  return (Object.values(Locales) as string[]).includes(value);
}

function toSalesforceLocale(locale: string): SalesforceLocales | undefined {
  return isLocale(locale) ? LocaleToSalesforceLocale[locale] : undefined;
}

export function generateSalesforceLocaleOptions(
  enabledLocales?: string[],
  currentValue?: SalesforceLocales,
) {
  const mappedEnabledSalesforceLocales =
    enabledLocales
      ?.map(toSalesforceLocale)
      .filter((locale): locale is SalesforceLocales => !!locale) ?? [];

  const allSalesforceLocales = Object.values(SalesforceLocales);

  const availableLocales =
    mappedEnabledSalesforceLocales.length > 0
      ? allSalesforceLocales.filter((sfLocale) =>
          mappedEnabledSalesforceLocales.includes(sfLocale),
        )
      : allSalesforceLocales;

  const enUsFirst = availableLocales.filter(
    (locale) => locale === SalesforceLocales.EN_US,
  );
  const rest = availableLocales.filter(
    (locale) => locale !== SalesforceLocales.EN_US,
  );

  const orderedLocales = [...enUsFirst, ...rest];

  const values =
    currentValue && !orderedLocales.includes(currentValue)
      ? [currentValue, ...orderedLocales]
      : orderedLocales;

  return values.map((value) => ({
    label: value.replaceAll('_', '-'),
    value,
  }));
}

export const FanreelCustomizationPlatformTypeLabels = {
  [PlatformType.SHOPIFY_HOSTED]: 'Shopify Hosted',
  [PlatformType.SFCC_LOREAL]: 'SFCC (Legacy)',
  [PlatformType.SFCC_CONNECTOR]: 'SFCC Connector',
};

export function generateCustomizationPlatformTypeOptions(
  includeSfccConnector: boolean,
) {
  return [
    {
      label:
        FanreelCustomizationPlatformTypeLabels[PlatformType.SHOPIFY_HOSTED],
      value: PlatformType.SHOPIFY_HOSTED,
    },
    ...(includeSfccConnector
      ? [
          {
            label:
              FanreelCustomizationPlatformTypeLabels[
                PlatformType.SFCC_CONNECTOR
              ],
            value: PlatformType.SFCC_CONNECTOR,
          },
        ]
      : []),
    {
      label: FanreelCustomizationPlatformTypeLabels[PlatformType.SFCC_LOREAL],
      value: PlatformType.SFCC_LOREAL,
    },
  ];
}

export interface IImageAspectRatioOption {
  label: FanreelCustomizationImageAspectRatios;
  value: FanreelCustomizationImageAspectRatios;
}

export class FanreelCustomizationAction {
  public typ: FanreelCustomizationActionType;
  public payload: any;

  constructor(typ: FanreelCustomizationActionType, payload: unknown = {}) {
    this.typ = typ;
    this.payload = payload;
  }
}

export function convertGalleryToExperience(
  gallery: ISelfServeGallery,
): IExperienceDto {
  return {
    experienceDataSource: gallery.reelId,
    experienceDataSourceId: gallery.dataSourceId,
    experienceLayout: gallery.layoutType,
    experienceType: gallery.containerType,
    experienceCustomizationOptions: gallery.customizationOptions,
  };
}

export enum HoverStateTheme {
  DARK = 'Dark',
  LIGHT = 'Light',
  CUSTOM = 'Custom',
}

export enum HoverThemeColors {
  BLACK = '#000000',
  WHITE = '#FFFFFF',
}

export enum ModalColors {
  BLACK = '#000000',
  WHITE = '#FFFFFF',
}
